import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { Role } from 'src/app/components/catalogs/roles/services/role';

import { Kind, RSF, RSFAmendment, RSFRoleNotification } from '../../../rsf';
import { Field } from '../../../rsf.service';
import { RSFRoleNotificationSetting, RSFSettings, RSFSettingType } from '../../../rsf-settings/rsf-settings';
import { RsfMainSBaseComponent } from '../../rsf-main-s-base/rsf-main-s-base.component';
import { Roles } from 'src/app/common/enumerations/enumerations';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';

@Component({
  selector: 'rsf-main-s1-notifications',
  templateUrl: './rsf-main-s1-notifications.component.html',
  styleUrls: ['./rsf-main-s1-notifications.component.scss']
})
export class RsfMainS1NotificationsComponent extends RsfMainSBaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() rsfProxy?: RSF | RSFAmendment | null;
  @Input() control?: FormControl;

  @Output() changed = new EventEmitter<RSFRoleNotification[]>();

  roles?: Role[];
  roles$!: Observable<Role[]>;
  rolesSubs!: Subscription;

  scientists?: User[] = [];

  field = Field;
  hilite?: boolean;

  rsfSettings?: RSFSettings[];
  rsfSettingsFiltered?: RSFSettings[];
  rsfSettingsSubs!: Subscription;

  rsfRoleNotificationSettings?: RSFRoleNotificationSetting[];

  roleNotifications?: RSFRoleNotification[];

  ngOnInit(): void {
    this.loadRSFSettings();
  }

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes['rsfProxy']) {
      this.initializeArray();
      this.setValues();
      this.loadScientists();
    }
  }

  override ngOnDestroy(): void {
    this.rsfSettingsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  /**
   * Loads RSF settings from the store and initializes role notifications.
   * Subscribes to settings data, filters by notification type, parses values, and sorts by order.
   */
  loadRSFSettings() {
    this.rsfSettingsSubs = this.store.select(state => state.RSFSettings.data).subscribe(data => {
      if (data?.length) {
        // Store and filter settings to notification types
        this.rsfSettings = data;
        this.rsfSettingsFiltered = this.rsfSettings?.filter(s => s.type == RSFSettingType.Notifications);
        this.rsfRoleNotificationSettings = [];
        // Parse default values from filtered settings
        this.rsfSettingsFiltered?.forEach(s => {
          const roleNotificationSetting = this.utils.JSONparse(s.defaultValue) as RSFRoleNotificationSetting;
          roleNotificationSetting.order = roleNotificationSetting.order ?? s.order ?? 0;
          this.rsfRoleNotificationSettings?.push(roleNotificationSetting);
        });
        // Transform to role notification objects and sort by order
        this.roleNotifications = this.rsfRoleNotificationSettings.sort((a, b) => this.utils.sort(a.order, b.order)).map(r => {
          return { roleID: r.roleID, checked: false, name: r.name } as RSFRoleNotification;
        });
        this.loadRoles();
      }
    });
  }

  loadRoles() {
    this.roles$ = this.store.select(state => state.Roles.data);
    this.rolesSubs = this.roles$.subscribe(data => {
      if (data?.length) {
        this.roles = data;
        this.initializeArray();
      }
    });
  }

  /**
   * Loads beamline scientists for the original RSF represented by the current view.
   * History views skip live lookups.
   */
  loadScientists() {
    if (this.status?.isLogView) {
      // History view renders saved snapshots and should not call live notification lookups.
      this.scientists = [];
      return;
    }

    const rsfID = this.rsfProxy?.kind == Kind.RSFAmendment
      ? (this.rsfProxy as RSFAmendment).rsfID
      : this.rsfProxy?.id;
    if (!rsfID) {
      this.scientists = [];
      return;
    };
    // Amendment IDs are not valid RSF IDs for the scientist endpoint.
    this.service?.getScienstists(rsfID).toPromise().then(data => {
      this.scientists = data;
    });
  }

  initializeArray() {
    this.roleNotifications?.forEach(n => {
      n.role = this.roles?.find(r => r.id == n.roleID);
      n.changed = false;
      n.checked = false;
    });
  }


  /**
   * Populates role notification properties with resolved values and determines if amendments have changed.
   * Matches roles, checks, and RSF IDs; detects changes when viewing amendments.
   */
  setValues() {
    // Populate role references, checked status, and RSF IDs for each notification
    this.roleNotifications?.forEach(n => {
      n.role = this.roles?.find(r => r.id == n.roleID);
      n.checked = this.rsfProxy?.rsfRoleNotifications?.find(r => r.roleID == n.roleID)?.checked;
      n.rsfID = this.rsfProxy?.id ?? 0;
      n.rsfAmendmentID = this.rsfProxy?.id ?? 0;

      // Compare amendment values to detect changes
      if (this.status?.isViewingAmendment && this.rsfAmendment && this.rsfProxy?.kind == Kind.RSFAmendment) {
        const o = this.rsfProxy.rsf?.rsfRoleNotifications?.find(r => r.roleID == n.roleID);
        n.changed = (o?.checked ?? false) != (n.checked ?? false);
      }
    });
    // Flag if any notifications have changes
    this.hilite = this.roleNotifications?.some(n => n.changed);
  }

  onCheckboxChange() {
    this.changed.emit(this.roleNotifications);
    this.control?.markAsDirty();
    this.control?.markAsTouched();
  }

  /**
   * Parses notification conditions from the RSF proxy and synchronizes role notification states.
   * Updates checked status for each role notification and emits the changes.
   */
  checkConditions() {
    if (this.rsfProxy) {
      // Parse conditions via service
      this.service?.parseNotificationConditions(this.utils.Serialize(this.rsfProxy) as RSF).subscribe((data) => {
        var rsf = data;
        // Sync checked state with response data
        rsf.rsfRoleNotifications.forEach(n => {
          var notification = this.roleNotifications?.find(r => r.roleID == n.roleID);
          if (notification) notification.checked = n.checked;
        });
        // Emit updated notifications
        this.changed.emit(this.roleNotifications);
      });
    }
  }

  getUsersTooltip(n: RSFRoleNotification): string {
    if (!n.checked) return '';
    var result = '';
    if (n.roleID != Roles.BLSCI) {
      var users = (this.users.filter(u => u.userRole?.map(ur => ur.roleID).includes(n.roleID)).map(u => u?.name) || []);
      if (users.length > 0) {
        result = users.join('<br>');
      }
    }
    else {
      result = this.scientists?.map(s => s.name).join('<br>') || '';
    }
    return result;
  }

}
